/**
 * Clase para manejar impresoras Bluetooth térmicas
 * Compatible con impresoras ESC/POS
 */

class BluetoothPrinter {
  constructor() {
    this.device = null;
    this.characteristic = null;
  }

  /**
   * Codifica texto como Latin-1 (1 byte por carácter).
   * Evita que TextEncoder convierta ñ/á/é a 2 bytes UTF-8 que la impresora
   * no puede interpretar. Con la code page WPC1252 activa, cada charCode
   * 0–255 corresponde exactamente al byte correcto (ñ = 0xF1, á = 0xE1, etc.)
   */
  _encodeLatin1(text) {
    const bytes = new Uint8Array(text.length);
    for (let i = 0; i < text.length; i++) {
      bytes[i] = text.charCodeAt(i) & 0xFF;
    }
    return bytes;
  }

  /**
   * Verifica si el navegador soporta Web Bluetooth API
   */
  isBluetoothAvailable() {
    return 'bluetooth' in navigator;
  }

  /**
   * Conecta con una impresora Bluetooth
   */
  async connect() {
    if (!this.isBluetoothAvailable()) {
      throw new Error('Web Bluetooth API no está disponible en este navegador');
    }

    try {
      // Solicitar dispositivo Bluetooth
      this.device = await navigator.bluetooth.requestDevice({
        filters: [
          { services: ['000018f0-0000-1000-8000-00805f9b34fb'] },
        ],
        optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
      });

      // Conectar al servidor GATT
      const server = await this.device.gatt.connect();
      
      // Obtener el servicio
      const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
      
      // Obtener la característica de escritura
      this.characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');
      
      return true;
    } catch (error) {
      console.error('❌ Error al conectar con la impresora:', error);
      // Preservar el tipo de error para que el llamador pueda distinguirlo
      const msg = error?.message?.toLowerCase() || '';
      if (error?.name === 'NotFoundError' || msg.includes('user cancelled') || msg.includes('user denied') || msg.includes('no device selected')) {
        throw new Error('User cancelled');
      }
      throw new Error('No se pudo conectar con la impresora Bluetooth');
    }
  }

  /**
   * Verifica si está conectado
   */
  isConnected() {
    return this.device && this.device.gatt.connected;
  }

  /**
   * Desconecta de la impresora
   */
  disconnect() {
    if (this.device && this.device.gatt.connected) {
      this.device.gatt.disconnect();
    }
  }

  /**
   * Envía datos en chunks para no exceder 512 bytes
   */
  async sendDataInChunks(data) {
    if (!this.isConnected()) {
      throw new Error('No hay conexión con la impresora');
    }

    // 20 bytes = MTU mínimo garantizado en BLE (ATT payload sin negociación).
    // 512 bytes funciona en PC pero se trunca en Android móvil.
    const chunkSize = 20;
    const buffer = new Uint8Array(data);

    for (let i = 0; i < buffer.length; i += chunkSize) {
      const chunk = buffer.slice(i, i + chunkSize);
      await this.characteristic.writeValue(chunk);
      // Pequeño delay para no saturar la impresora
      await new Promise(resolve => setTimeout(resolve, 20));
    }
  }

  /**
   * Imprime el resumen de una bolsa de alistamiento
   */
  async printBolsaResumen(bolsa, cliente, alistador, consecutivo) {
    if (!this.isConnected()) {
      await this.connect();
    }

    try {
      // Construir contenido del recibo de forma compacta
      let content = '\x1B\x40\x1B\x74\x10'; // Inicializar impresora
      content += '\x1B\x61\x01\x1B\x21\x30' +cliente.nombre + '\n';
      content += '\x1B\x21\x00\x1B\x61\x00';
      content += '--------------------------------\n';
      content += `Consecutivo: ${consecutivo}\n`;
      content += `BOLSA: ${bolsa.numero}\n`;
      content += `Alistador: ${alistador.name || alistador.nombre}\n`;
      
      const now = new Date();
      const fecha = now.toLocaleDateString('es-CO');
      const hora = now.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: false });
      content += `Fecha: ${fecha} ${hora}\n`;
      content += '--------------------------------\n';

      // Items de la bolsa
      bolsa.items.forEach((item, index) => {
        const nombre = item.nombre.length > 20 
          ? item.nombre.substring(0, 17) + '...' 
          : item.nombre;
        
        // Formatear con nombre a la izquierda y cantidad a la derecha
        const nombreConNumero = `${index + 1}. ${nombre}`;
        const cantidad = `x${item.cantidad}`;
        
        // Calcular espacios para alinear (ancho total de 32 caracteres)
        const espacios = 32 - nombreConNumero.length - cantidad.length;
        const padding = espacios > 0 ? ' '.repeat(espacios) : ' ';
        
        content += nombreConNumero + padding + cantidad + '\n';
      });

      // Totales
      content += '================================\n';
      content += '\x1B\x21\x10\x1B\x61\x01';
      content += `${bolsa.totalItems} PRENDAS\n`;
      content += `${bolsa.pesoTotal}g TOTAL\n`;
      content += '\x1B\x21\x00\x1B\x61\x00';
      content += '================================\n';

      content += '\x1B\x64\x03\x1D\x56\x00'; // Avanzar 3 líneas y cortar

      // Convertir a bytes y enviar en chunks
      await this.sendDataInChunks(this._encodeLatin1(content));

    } catch (error) {
      console.error('❌ Error al imprimir bolsa:', error);
      throw error;
    }
  }

  /**
   * Imprime un consolidado de bolsas (resumen de todas las bolsas)
   * @param {Array} bolsas - Array de bolsas con totales
   * @param {Object} cliente - Datos del cliente
   * @param {Object} alistador - Datos del alistador
   * @param {String} consecutivo - Consecutivo del alistamiento
   * @param {Array} items - Array de items del alistamiento (opcional)
   */
  async printConsolidadoBolsas(bolsas, cliente, alistador, consecutivo, items = []) {
    if (!this.isConnected()) {
      await this.connect();
    }

    try {
      const hora = new Date().toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: false });
      let content = '\x1B\x40\x1B\x74\x10'; // Inicializar impresora
      content += '\x1B\x61\x01\x1B\x21\x30' + cliente.nombre + '\n';
      content += '\x1B\x21\x00\x1B\x61\x00';
      content += '--------------------------------\n';
      content += `Consecutivo: ${consecutivo}\n`;
      content += `Alistador: ${alistador.name || alistador.nombre}\n`;
      content += `Fecha: ${new Date().toLocaleDateString('es-CO') + ' ' + hora}\n`;

      content += '================================\n';

      // Listar cada bolsa con sus items
      bolsas.forEach((bolsa, index) => {
        content += `\n\x1B\x21\x08BOLSA #${bolsa.numero}\x1B\x21\x00\n`;
        content += '- - - - - - - - - - - - - - - -\n';
        
        // Obtener items de esta bolsa desde el array de items
        const itemsDeBolsa = items.filter(item => item.bolsa === bolsa.numero);
        
        // Listar items de la bolsa
        if (itemsDeBolsa && itemsDeBolsa.length > 0) {
          itemsDeBolsa.forEach((item, idx) => {
            const nombre = item.nombre.length > 20 
              ? item.nombre.substring(0, 17) + '...' 
              : item.nombre;
            
            const nombreConNumero = ` ${nombre}`;
            const cantidad = `x${item.cantidad}`;
            
            // Calcular espacios para alinear
            const espacios = 32 - nombreConNumero.length - cantidad.length;
            const padding = espacios > 0 ? ' '.repeat(espacios) : ' ';
            
            content += nombreConNumero + padding + cantidad + '\n';
          });
        }
      });

      // Totales generales
      const totalItemsGeneral = bolsas.reduce((sum, b) => sum + b.totalItems, 0);
      const pesoTotalGeneral = bolsas.reduce((sum, b) => sum + b.pesoTotal, 0);

      content += '\n================================\n';
      content += '\x1B\x21\x10\x1B\x61\x01';
      content += `${bolsas.length} BOLSAS\n`;
      content += `${totalItemsGeneral} PRENDAS\n`;
      content += `${pesoTotalGeneral}g TOTAL\n`;
      content += '\x1B\x21\x00\x1B\x61\x00';
      content += '================================\n';

      content += '\x1B\x64\x03\x1D\x56\x00'; // Avanzar 3 líneas y cortar

      // Convertir a bytes y enviar en chunks
      await this.sendDataInChunks(this._encodeLatin1(content));

    } catch (error) {
      console.error('❌ Error al imprimir consolidado de bolsas:', error);
      throw error;
    }
  }

  /**
   * Imprime el consolidado de items de un alistamiento
   */
  async printConsolidadoAlistamiento(alistamiento) {
    if (!this.isConnected()) {
      await this.connect();
    }

    try {
      // Construir contenido del consolidado
      const hora = new Date().toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: false });
      let content = '\x1B\x40\x1B\x74\x10'; // Inicializar impresora
      content += '\x1B\x61\x01\x1B\x21\x30' + alistamiento.cliente.nombre + '\n';
      content += '\x1B\x21\x00\x1B\x61\x00';
      content += '--------------------------------\n';
      content += `Consecutivo: ${alistamiento.consecutivo}\n`;
      content += `Alistador: ${alistamiento.alistador.nombre}\n`;
      content += `Fecha: ${new Date(alistamiento.fechaCreacion).toLocaleDateString() + ' ' + hora}\n`;
      content += '--------------------------------\n\n';

      // Consolidar items por prenda
      const itemsConsolidados = {};
      alistamiento.items.forEach(item => {
        if (itemsConsolidados[item.prendaId]) {
          itemsConsolidados[item.prendaId].cantidad += item.cantidad;
        } else {
          itemsConsolidados[item.prendaId] = {
            nombre: item.nombre,
            cantidad: item.cantidad,
            peso: item.peso
          };
        }
      });

      // Convertir a array y ordenar por nombre
      const itemsArray = Object.values(itemsConsolidados).sort((a, b) => 
        a.nombre.localeCompare(b.nombre)
      );

      // Imprimir items consolidados
      content += '\x1B\x21\x08ITEMS CONSOLIDADOS\x1B\x21\x00\n';
      content += '================================\n';
      
      itemsArray.forEach((item, index) => {
        const nombre = item.nombre.length > 20 
          ? item.nombre.substring(0, 17) + '...' 
          : item.nombre;
        
        const nombreConNumero = `${index + 1}. ${nombre}`;
        const cantidad = `x${item.cantidad}`;
        
        // Calcular espacios para alinear
        const espacios = 32 - nombreConNumero.length - cantidad.length;
        const padding = espacios > 0 ? ' '.repeat(espacios) : ' ';
        
        content += nombreConNumero + padding + cantidad + '\n';
      });

      // Totales
      content += '================================\n';
      content += '\x1B\x21\x10\x1B\x61\x01';
      content += `${alistamiento.totalItems} PRENDAS\n`;
      content += `${alistamiento.pesoTotal}g TOTAL\n`;
      content += `${alistamiento.bolsas?.length || 0} BOLSAS\n`;
      content += '\x1B\x21\x00\x1B\x61\x00';
      content += '================================\n';

      if (alistamiento.observaciones) {
        const obs = alistamiento.observaciones.length > 100 
          ? alistamiento.observaciones.substring(0, 97) + '...' 
          : alistamiento.observaciones;
        content += `\nObs: ${obs}\n`;
      }
      content += '\x1B\x64\x03\x1D\x56\x00'; // Avanzar 3 líneas y cortar

      // Convertir a bytes y enviar en chunks
      await this.sendDataInChunks(this._encodeLatin1(content));

    } catch (error) {
      console.error('❌ Error al imprimir consolidado:', error);
      throw error;
    }
  }
  /**
   * Genera el contenido de un recibo de recepción de ropa
   * @param {object} recepcion - datos de la recepción
   * @param {string} copia - 'CLIENTE' o 'LAVANDERIA'
   */
  _buildRecepcionContent(recepcion, copia) {
    const hora = new Date().toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: false });
    const fecha = recepcion.fecha_recepcion
      ? new Date(recepcion.fecha_recepcion).toLocaleDateString('es-CO')
      : new Date().toLocaleDateString('es-CO');
    const tipoCobro = recepcion.tipoCobro || 'cantidades';
    const toKg = (g) => g ? (g / 1000).toFixed(2) : '0.00';

    let content = '\x1B\x40\x1B\x74\x10'; // Inicializar impresora
    // Consecutivo como título centrado y en negrita
    content += '\x1B\x61\x01\x1B\x21\x30';
    content += `${recepcion.consecutivo}\n`;
    content += '\x1B\x21\x00';
    content += `COPIA ${copia}\n`;
    content += '\x1B\x61\x00';
    content += '================================\n';

    // Info del cliente
    content += '\x1B\x21\x08' + (recepcion.cliente?.nombre || '') + '\x1B\x21\x00\n';
    if (recepcion.cliente?.nit) {
      content += `NIT: ${recepcion.cliente.nit}\n`;
    }
    content += '--------------------------------\n';
    content += `Fecha: ${fecha} ${hora}\n`;
    content += `Recibido por: ${recepcion.recibido_por}\n`;
    if (recepcion.lavanderia?.nombre) {
      content += `Centro: ${recepcion.lavanderia.nombre}\n`;
    }
    content += '--------------------------------\n';

    content += `Tipo cobro: ${tipoCobro === 'kilos' ? 'Por Kilos' : 'Por Cantidades'}\n`;
    content += '================================\n';

    // Items
    if (tipoCobro === 'kilos') {
      (recepcion.prendas || []).forEach((item, index) => {
        const nombre = item.nombre.length > 20
          ? item.nombre.substring(0, 17) + '...'
          : item.nombre;
        const peso = `${toKg(item.peso_real)}kg`;
        const nombreConNumero = `${index + 1}. ${nombre}`;
        const espacios = 32 - nombreConNumero.length - peso.length;
        const padding = espacios > 0 ? ' '.repeat(espacios) : ' ';
        content += nombreConNumero + padding + peso + '\n';
      });
    } else {
      // Cantidades: solo nombre y cantidad, sin peso
      (recepcion.prendas || []).forEach((item, index) => {
        const nombre = item.nombre.length > 20
          ? item.nombre.substring(0, 17) + '...'
          : item.nombre;
        const cantidad = `x${item.cantidad}`;
        const nombreConNumero = `${index + 1}. ${nombre}`;
        const espacios = 32 - nombreConNumero.length - cantidad.length;
        const padding = espacios > 0 ? ' '.repeat(espacios) : ' ';
        content += nombreConNumero + padding + cantidad + '\n';
      });
    }

    // Totales
    content += '================================\n';
    content += '\x1B\x21\x10\x1B\x61\x01';
    if (tipoCobro === 'kilos') {
      content += `${toKg(recepcion.total_peso_real)}kg PESO TOTAL\n`;
    } else {
      content += `${recepcion.total_prendas} PRENDAS\n`;
    }
    content += '\x1B\x21\x00\x1B\x61\x00';
    content += '================================\n';

    if (recepcion.observaciones) {
      const obs = recepcion.observaciones.length > 100
        ? recepcion.observaciones.substring(0, 97) + '...'
        : recepcion.observaciones;
      content += `\nObs: ${obs}\n`;
    }

    // Firma solo en copia lavandería
    if (copia === 'LAVANDERIA') {
      content += '\n\n\n';
      content += '________________________________\n';
      content += '\x1B\x61\x01';
      content += 'Firma del Cliente\n';
      content += '\x1B\x61\x00';
      content += '\n\n';
      content += '________________________________\n';
      content += '\x1B\x61\x01';
      content += 'Firma Quien Recibe\n';
      content += '\x1B\x61\x00';
    }

    content += '\x1B\x64\x03\x1D\x56\x00'; // Avanzar 3 líneas y cortar
    return content;
  }

  /**
   * Genera el contenido ESC/POS para una recolección de ropa sucia
   * @param {object} data - datos de la recolección
   * @param {string} copia - 'CLIENTE' o 'LAVANDERIA'
   */
  _buildRecoleccionContent(data, copia) {
    const hora = new Date().toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: false });
    const fecha = data.fecha_recoleccion
      ? new Date(data.fecha_recoleccion).toLocaleDateString('es-CO')
      : new Date().toLocaleDateString('es-CO');

    let content = '\x1B\x40\x1B\x74\x10'; // Inicializar impresora
    // Título centrado y en negrita
    content += '\x1B\x61\x01\x1B\x21\x30';
    content += `${data.consecutivo}\n`;
    content += '\x1B\x21\x00';
    content += `COPIA ${copia}\n`;
    content += '\x1B\x61\x00';
    content += '================================\n';
    content += '\x1B\x61\x01RECOLECCION ROPA SUCIA\x1B\x61\x00\n';
    content += '================================\n';

    // Info del cliente
    content += '\x1B\x21\x08' + (data.cliente?.nombre || '') + '\x1B\x21\x00\n';
    if (data.cliente?.nit) {
      content += `NIT: ${data.cliente.nit}\n`;
    }
    content += '--------------------------------\n';
    content += `Fecha: ${fecha} ${hora}\n`;
    content += `Conductor: ${data.conductor?.nombre || '-'}\n`;
    if (data.recibe?.nombre && data.recibe.nombre !== 'N/A') {
      content += `Entrega: ${data.recibe.nombre}\n`;
    }
    if (data.lavanderia?.nombre) {
      content += `Centro: ${data.lavanderia.nombre}\n`;
    }
    content +=  `Tipo: ${data.tipoRecoleccion}\n`;
    
    content += '================================\n';

    // Items — agrupar por familia si la info está disponible
    const items = data.items || [];
    let totalPrendas = 0;
    const tienesFamilias = items.some((i) => i.familia?.nombre);

    const printItem = (item, index) => {
      const nombre = (item.nombre || '').length > 20
        ? (item.nombre || '').substring(0, 17) + '...'
        : (item.nombre || '');
      const cant = item.cantidad_confirmada ?? item.cantidad_alistada ?? item.cantidad ?? 0;
      totalPrendas += cant;
      const cantidad = `x${cant}`;
      const nombreConNumero = `${nombre}`;
      const espacios = 32 - nombreConNumero.length - cantidad.length;
      const padding = espacios > 0 ? ' '.repeat(espacios) : ' ';
      return nombreConNumero + padding + cantidad + '\n';
    };

    if (tienesFamilias) {
      // Agrupar por familia
      const groups = new Map();
      items.forEach((item) => {
        const key = item.familia?.nombre || 'Sin Familia';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
      });
      // Ordenar: familias con nombre primero, Sin Familia al final
      const sorted = Array.from(groups.entries()).sort(([a], [b]) => {
        if (a === 'Sin Familia') return 1;
        if (b === 'Sin Familia') return -1;
        return a.localeCompare(b);
      });
      let globalIdx = 0;
      sorted.forEach(([familiaName, groupItems]) => {
        // Cabecera de familia
        const label = familiaName.toUpperCase();
        const pad = Math.max(0, Math.floor((32 - label.length - 4) / 2));
        const familiaHeader = '-'.repeat(pad) + '  ' + label + '  ' + '-'.repeat(pad);
        content += '\x1B\x21\x08' + familiaHeader.substring(0, 32) + '\x1B\x21\x00\n';
        // Items del grupo
        let totalGrupo = 0;
        groupItems.forEach((item) => {
          content += printItem(item, ++globalIdx);
          totalGrupo += item.cantidad_confirmada ?? item.cantidad_alistada ?? item.cantidad ?? 0;
        });
        // Total de la familia alineado a la derecha
        const totalLabel = 'Total:';
        const totalVal = `${totalGrupo} uds`;
        const espaciosTotal = 32 - totalLabel.length - totalVal.length;
        const paddingTotal = espaciosTotal > 0 ? ' '.repeat(espaciosTotal) : ' ';
        content += '\x1B\x21\x08' + totalLabel + paddingTotal + totalVal + '\x1B\x21\x00\n';
      });
    } else {
      // Lista plana (sin familia)
      items.forEach((item, index) => {
        content += printItem(item, index + 1);
      });
    }

    // Total
    content += '================================\n';
    content += '\x1B\x21\x10\x1B\x61\x01';
    content += `${totalPrendas} PRENDAS\n`;
    content += '\x1B\x21\x00\x1B\x61\x00';
    content += '================================\n';

    if (data.observaciones) {
      const obs = data.observaciones.length > 100
        ? data.observaciones.substring(0, 97) + '...'
        : data.observaciones;
      content += `\nObs: ${obs}\n`;
    }

    // Firmas solo en copia lavandería
    if (copia === 'LAVANDERIA') {
      content += '\n\n\n';
      content += '________________________________\n';
      content += '\x1B\x61\x01';
      content += 'Firma Conductor\n';
      content += '\x1B\x61\x00';
      content += '\n\n\n\n';
      content += '________________________________\n';
      content += '\x1B\x61\x01';
      content += 'Firma Quien Entrega\n';
      content += '\x1B\x61\x00';
    }

    content += '\x1B\x64\x03\x1D\x56\x00'; // Avanzar 3 líneas y cortar
    return content;
  }

  /**
   * Genera el contenido ESC/POS para una entrega de ropa limpia
   * @param {object} data - datos de la entrega
   * @param {string} copia - 'CLIENTE' o 'LAVANDERIA'
   */
  _buildEntregaContent(data, copia) {
    const hora = new Date().toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: false });
    const fecha = data.fecha_entrega
      ? new Date(data.fecha_entrega).toLocaleDateString('es-CO')
      : new Date().toLocaleDateString('es-CO');

    let content = '\x1B\x40\x1B\x74\x10'; // Inicializar impresora
    content += '\x1B\x61\x01\x1B\x21\x30';
    content += `${data.consecutivo}\n`;
    content += '\x1B\x21\x00';
    content += `COPIA ${copia}\n`;
    content += '\x1B\x61\x00';
    content += '================================\n';
    content += '\x1B\x61\x01ENTREGA ROPA LIMPIA\x1B\x61\x00\n';
    content += '================================\n';

    // Info del cliente
    content += '\x1B\x21\x08' + (data.cliente?.nombre || '') + '\x1B\x21\x00\n';
    if (data.cliente?.nit) {
      content += `NIT: ${data.cliente.nit}\n`;
    }
    content += '--------------------------------\n';
    content += `Fecha: ${fecha} ${hora}\n`;
    content += `Conductor: ${data.conductor?.nombre || '-'}\n`;
    if (data.recibe?.nombre && data.recibe.nombre !== 'N/A') {
      content += `Recibe: ${data.recibe.nombre}\n`;
    }
    if (data.lavanderia?.nombre) {
      content += `Centro: ${data.lavanderia.nombre}\n`;
    }
    if (data.tipoEntrega) {
      content += `Tipo: ${data.tipoEntrega}\n`;
    }
    content += '================================\n';

    // Items — filtrar cantidad 0 y agrupar por familia si la info está disponible
    const items = (data.items || []).filter(
      (i) => (i.cantidad_confirmada ?? i.cantidad_alistada ?? i.cantidad ?? 0) > 0
    );
    let totalPrendas = 0;
    const tienesFamilias = items.some((i) => i.familia?.nombre);

    const printItem = (item) => {
      const nombre = (item.nombre || '').length > 20
        ? (item.nombre || '').substring(0, 17) + '...'
        : (item.nombre || '');
      const cant = item.cantidad_confirmada ?? item.cantidad_alistada ?? item.cantidad ?? 0;
      totalPrendas += cant;
      const cantidad = `x${cant}`;
      const espacios = 32 - nombre.length - cantidad.length;
      const padding = espacios > 0 ? ' '.repeat(espacios) : ' ';
      return nombre + padding + cantidad + '\n';
    };

    if (tienesFamilias) {
      const groups = new Map();
      items.forEach((item) => {
        const key = item.familia?.nombre || 'Sin Familia';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
      });
      const sorted = Array.from(groups.entries()).sort(([a], [b]) => {
        if (a === 'Sin Familia') return 1;
        if (b === 'Sin Familia') return -1;
        return a.localeCompare(b);
      });
      sorted.forEach(([familiaName, groupItems]) => {
        const label = familiaName.toUpperCase();
        const pad = Math.max(0, Math.floor((32 - label.length - 4) / 2));
        const familiaHeader = '-'.repeat(pad) + '  ' + label + '  ' + '-'.repeat(pad);
        content += '\x1B\x21\x08' + familiaHeader.substring(0, 32) + '\x1B\x21\x00\n';
        let totalGrupo = 0;
        groupItems.forEach((item) => {
          content += printItem(item);
          totalGrupo += item.cantidad_confirmada ?? item.cantidad_alistada ?? item.cantidad ?? 0;
        });
        const totalLabel = 'Total:';
        const totalVal = `${totalGrupo} uds`;
        const esp = 32 - totalLabel.length - totalVal.length;
        const paddingTotal = esp > 0 ? ' '.repeat(esp) : ' ';
        content += '\x1B\x21\x08' + totalLabel + paddingTotal + totalVal + '\x1B\x21\x00\n';
      });
    } else {
      items.forEach((item) => { content += printItem(item); });
    }

    // Total
    content += '================================\n';
    content += '\x1B\x21\x10\x1B\x61\x01';
    content += `${totalPrendas} PRENDAS\n`;
    content += '\x1B\x21\x00\x1B\x61\x00';
    content += '================================\n';

    if (data.observaciones) {
      const obs = data.observaciones.length > 100
        ? data.observaciones.substring(0, 97) + '...'
        : data.observaciones;
      content += `\nObs: ${obs}\n`;
    }

    // Firmas solo en copia lavandería
    if (copia === 'LAVANDERIA') {
      content += '\n\n\n';
      content += '________________________________\n';
      content += '\x1B\x61\x01';
      content += 'Firma Conductor\n';
      content += '\x1B\x61\x00';
      content += '\n\n\n\n';
      content += '________________________________\n';
      content += '\x1B\x61\x01';
      content += 'Firma Quien Recibe\n';
      content += '\x1B\x61\x00';
    }

    content += '\x1B\x64\x03\x1D\x56\x00'; // Avanzar 3 líneas y cortar
    return content;
  }

  /**
   * Imprime el recibo de entrega de ropa limpia (dos copias)
   */
  async printEntrega(data) {
    if (!this.isConnected()) {
      await this.connect();
    }

    try {
      // Copia 1: Cliente
      const contentCliente = this._buildEntregaContent(data, 'CLIENTE');
      await this.sendDataInChunks(this._encodeLatin1(contentCliente));

      // Delay entre copias
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Copia 2: Lavandería (con espacio para firmas)
      const contentLavanderia = this._buildEntregaContent(data, 'LAVANDERIA');
      await this.sendDataInChunks(this._encodeLatin1(contentLavanderia));

    } catch (error) {
      console.error('❌ Error al imprimir entrega:', error);
      throw error;
    }
  }

  /**
   * Imprime el recibo de recolección de ropa sucia (dos copias)
   */
  async printRecoleccion(data) {
    if (!this.isConnected()) {
      await this.connect();
    }

    try {
      // Copia 1: Cliente
      const contentCliente = this._buildRecoleccionContent(data, 'CLIENTE');
      await this.sendDataInChunks(this._encodeLatin1(contentCliente));

      // Delay entre copias
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Copia 2: Lavandería (con espacio para firmas)
      const contentLavanderia = this._buildRecoleccionContent(data, 'LAVANDERIA');
      await this.sendDataInChunks(this._encodeLatin1(contentLavanderia));

    } catch (error) {
      console.error('❌ Error al imprimir recolección:', error);
      throw error;
    }
  }

  /**
   * Imprime el inventario pendiente de un cliente (una copia)
   * @param {object} cliente - { clienteNombre, items: [{ tipoPrenda, cantidad, descripcion }], totalPrendas }
   */
  async printInventarioCliente(cliente) {
    if (!this.isConnected()) {
      await this.connect();
    }

    try {
      const hora = new Date().toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: false });
      const fecha = new Date().toLocaleDateString('es-CO');

      let content = '\x1B\x40\x1B\x74\x10';
      content += '\x1B\x61\x01\x1B\x21\x30';
      content += 'INVENTARIO\n';
      content += '\x1B\x21\x00\x1B\x61\x00';
      content += '================================\n';
      content += '\x1B\x21\x08' + (cliente.clienteNombre || '') + '\x1B\x21\x00\n';
      content += '--------------------------------\n';
      content += `Fecha: ${fecha} ${hora}\n`;
      content += '================================\n';

      // Agrupar items por familia
      const groups = new Map();
      (cliente.items || []).forEach((item) => {
        const key = item.familia?.nombre || 'Sin Familia';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
      });
      const sorted = Array.from(groups.entries()).sort(([a], [b]) => {
        if (a === 'Sin Familia') return 1;
        if (b === 'Sin Familia') return -1;
        return a.localeCompare(b);
      });


      sorted.forEach(([familiaName, items]) => {
        // Cabecera de familia
        const label = familiaName.toUpperCase();
        const pad = Math.max(0, Math.floor((32 - label.length - 4) / 2));
        const familiaHeader = '-'.repeat(pad) + '  ' + label + '  ' + '-'.repeat(pad);
        content += '\x1B\x21\x08' + familiaHeader.substring(0, 32) + '\x1B\x21\x00\n';

        let totalGrupo = 0;
        items.forEach((item) => {
          const nombre = (item.tipoPrenda?.nombre || item.descripcion || 'Prenda').length > 20
            ? (item.tipoPrenda?.nombre || item.descripcion || 'Prenda').substring(0, 17) + '...'
            : (item.tipoPrenda?.nombre || item.descripcion || 'Prenda');
          const cant = `x${item.cantidad}`;
          const nombreConNumero = `${nombre}`;
          const espacios = 32 - nombreConNumero.length - cant.length;
          const padding = espacios > 0 ? ' '.repeat(espacios) : ' ';
          content += nombreConNumero + padding + cant + '\n';
          totalGrupo += item.cantidad || 0;
        });

        // Total del grupo alineado a la derecha
        const totalLabel = 'Total:';
        const totalVal = `${totalGrupo} uds`;
        const esp = 32 - totalLabel.length - totalVal.length;
        const pad2 = esp > 0 ? ' '.repeat(esp) : ' ';
        content += '\x1B\x21\x08' + totalLabel + pad2 + totalVal + '\x1B\x21\x00\n';
      });

      content += '================================\n';
      content += '\x1B\x21\x10\x1B\x61\x01';
      content += `${cliente.totalPrendas} PRENDAS\n`;
      content += '\x1B\x21\x00\x1B\x61\x00';
      content += '================================\n';
      content += '\x1B\x64\x03\x1D\x56\x00';

      await this.sendDataInChunks(this._encodeLatin1(content));

    } catch (error) {
      console.error('❌ Error al imprimir inventario de cliente:', error);
      throw error;
    }
  }

  /**
   * Imprime el ticket de recepción de ropa sucia (lote interno)
   */
  async printLoteRopaSucia(lote) {
    if (!this.isConnected()) {
      await this.connect();
    }

    try {
      const hora = new Date().toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: false });
      const fecha = lote.createdAt
        ? new Date(lote.createdAt).toLocaleDateString('es-CO')
        : new Date().toLocaleDateString('es-CO');

      let content = '\x1B\x40\x1B\x74\x10';
      content += '\x1B\x61\x01\x1B\x21\x30';
      content += `LOTE #${lote.numeroLote || '-'}\n`;
      content += '\x1B\x21\x00\x1B\x61\x00';
      content += '================================\n';

      content += '\x1B\x61\x01\x1B\x21\x08' + (lote.cliente?.nombre || '') + '\x1B\x21\x00\x1B\x61\x00\n';
      content += '--------------------------------\n';
      content += `Fecha: ${fecha} ${hora}\n`;
      content += `Lavador: ${lote.empleado?.nombre || '-'}\n`;
      content += '================================\n';

      if (lote.tipoPrenda?.nombre) {
        const nombrePrenda = lote.tipoPrenda.nombre.length > 22
          ? lote.tipoPrenda.nombre.substring(0, 19) + '...'
          : lote.tipoPrenda.nombre;
        content += `Tipo: ${nombrePrenda}\n`;
      }
      if (lote.maquina?.nombre) {
        content += `Lavadora: ${lote.maquina.nombre}\n`;
      }
      if (lote.programaLavado?.nombre) {
        const prog = lote.programaLavado.nombre.length > 22
          ? lote.programaLavado.nombre.substring(0, 19) + '...'
          : lote.programaLavado.nombre;
        content += `Programa: ${prog}\n`;
      }
      content += '================================\n';

      content += '\x1B\x21\x38\x1B\x61\x01';
      content += `${parseFloat(lote.pesoKg || 0).toFixed(0)} Kg\n`;
      content += '\x1B\x21\x00\x1B\x61\x00';
      content += '================================\n';

      if (lote.observaciones) {
        const obs = lote.observaciones.length > 100
          ? lote.observaciones.substring(0, 97) + '...'
          : lote.observaciones;
        content += `\nObs: ${obs}\n`;
      }

      content += '\x1B\x64\x03\x1D\x56\x00';

      await this.sendDataInChunks(this._encodeLatin1(content));
    } catch (error) {
      console.error('❌ Error al imprimir lote de ropa sucia:', error);
      throw error;
    }
  }

  /**
   * Imprime el recibo de recepción de ropa de cliente (dos copias)
   */
  async printRecepcionRopa(recepcion) {
    if (!this.isConnected()) {
      await this.connect();
    }

    try {
      // Copia 1: Cliente
      const contentCliente = this._buildRecepcionContent(recepcion, 'CLIENTE');
      await this.sendDataInChunks(this._encodeLatin1(contentCliente));

      // Delay entre copias
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Copia 2: Lavandería (con espacio para firma)
      const contentLavanderia = this._buildRecepcionContent(recepcion, 'LAVANDERIA');
      await this.sendDataInChunks(this._encodeLatin1(contentLavanderia));
    } catch (error) {
      console.error('❌ Error al imprimir recibo de recepción:', error);
      throw error;
    }
  }
}

// Instancia singleton
let printerInstance = null;

/**
 * Obtiene la instancia singleton de la impresora
 */
export const getPrinterInstance = () => {
  if (!printerInstance) {
    printerInstance = new BluetoothPrinter();
  }
  return printerInstance;
};

export default BluetoothPrinter;
